import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ComposedChart, Line, Legend,
} from 'recharts';
import { DollarSign, Loader2, RefreshCw, TrendingUp, TrendingDown, Users, AlertTriangle, Download, X, Clock, ChevronRight } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';

const RANGES = [4, 8, 12];
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const csvEscape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
const downloadCsv = (name, rows) => {
  const url = URL.createObjectURL(new Blob([rows.map(r => r.map(csvEscape).join(',')).join('\n')], { type: 'text/csv' }));
  const a = document.createElement('a'); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
};

/**
 * Worker Pay Analytics — shared by the Employer portal, Agency portal and the HRIS.
 * Weekly settled payouts + hours, overtime alerts, payee drill-down and CSV export.
 * Reads /api/org/analytics/payouts (org-scoped; HR users act as their organisation).
 */
export default function AgencyPayAnalytics() {
  const [weeks, setWeeks] = useState(8);
  const [otThreshold, setOtThreshold] = useState(40);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await apiCall(`/api/org/analytics/payouts?weeks=${weeks}&ot_threshold=${otThreshold}`)); }
    catch (e) { toast.error(e.message || 'Failed to load pay analytics'); }
    finally { setLoading(false); }
  }, [weeks, otThreshold]);
  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const series = data?.series || [];
  const workers = data?.workers || [];
  const overtime = data?.overtime || [];
  const delta = Number(summary.delta_pct || 0);
  const deltaUp = delta >= 0;
  const hasData = useMemo(() => series.some((s) => s.volume > 0 || s.hours > 0), [series]);

  const exportCsv = () => {
    const rows = [['Section', 'Week', 'Label', 'Worker', 'Volume ($)', 'Payouts', 'Hours', 'Overtime workers / over by (h)']];
    series.forEach(s => rows.push(['Weekly', s.week, s.label, '', s.volume, s.count, s.hours, s.overtime_workers]));
    workers.forEach(w => rows.push(['Payee', '', '', w.worker_name, w.volume, w.count, w.hours, w.overtime_weeks ? `${w.overtime_weeks} OT week(s)` : '']));
    overtime.forEach(o => rows.push(['Overtime', o.week, o.label, o.worker_name, '', '', o.hours, o.over_by]));
    downloadCsv(`pay-analytics-${weeks}w-${new Date().toISOString().slice(0, 10)}.csv`, rows);
    toast.success('CSV exported');
  };

  return (
    <div className="space-y-4" data-testid="agency-pay-analytics">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-semibold text-slate-900 flex items-center gap-2"><DollarSign className="w-4 h-4 text-indigo-600" /> Worker Pay Analytics</h3>
          <p className="text-xs text-slate-500 mt-0.5">Weekly settled payouts and clocked hours for your {data?.org_role === 'agent' ? 'agency roster' : 'workforce'}. Click a payee to drill down.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1" role="group" aria-label="Range selector">
            {RANGES.map((r) => (
              <button key={r} onClick={() => setWeeks(r)} className={`px-2.5 py-1 rounded-lg text-xs font-bold ${weeks === r ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`} data-testid={`agency-pay-range-${r}`}>{r}w</button>
            ))}
          </div>
          <label className="inline-flex items-center gap-1 text-xs text-slate-600">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> OT &gt;
            <input type="number" min={1} max={168} value={otThreshold} onChange={e => setOtThreshold(Math.max(1, Math.min(168, Number(e.target.value) || 40)))}
              className="w-14 px-1.5 py-0.5 border border-slate-200 rounded text-xs" data-testid="agency-pay-ot-threshold" /> h/wk
          </label>
          <button onClick={exportCsv} disabled={!data} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40" data-testid="agency-pay-export-csv">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
          <button onClick={load} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900" data-testid="agency-pay-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="This week" value={fmtMoney(summary.current_week_volume)} testid="agency-pay-kpi-current" accent />
        <Kpi label="Prior week" value={fmtMoney(summary.prior_week_volume)} testid="agency-pay-kpi-prior" />
        <div className="rounded-xl p-3 border bg-white border-slate-200" data-testid="agency-pay-kpi-delta">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
            {deltaUp ? <TrendingUp className="w-3.5 h-3.5 text-emerald-600" /> : <TrendingDown className="w-3.5 h-3.5 text-red-600" />} Week-over-week
          </div>
          <p className={`mt-1 text-lg font-semibold ${deltaUp ? 'text-emerald-600' : 'text-red-600'}`}>{deltaUp ? '+' : ''}{delta}%</p>
        </div>
        <Kpi label="Active payees" value={summary.active_workers || 0} testid="agency-pay-kpi-workers" icon={Users} />
        <div className={`rounded-xl p-3 border ${summary.overtime_alerts ? 'bg-amber-50 border-amber-300' : 'bg-white border-slate-200'}`} data-testid="agency-pay-kpi-overtime">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500"><AlertTriangle className={`w-3.5 h-3.5 ${summary.overtime_alerts ? 'text-amber-600' : ''}`} /> Overtime alerts</div>
          <p className={`mt-1 text-lg font-semibold ${summary.overtime_alerts ? 'text-amber-700' : 'text-slate-900'}`}>{summary.overtime_alerts || 0}</p>
          <p className="text-[10px] text-slate-500">{summary.total_hours || 0}h clocked · {summary.overtime_workers || 0} worker{summary.overtime_workers === 1 ? '' : 's'} over {data?.ot_threshold || otThreshold}h</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        {loading && !data ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div>
        ) : !hasData ? (
          <p className="text-xs text-slate-400 text-center py-10" data-testid="agency-pay-empty">No settled worker payouts in this window yet. Once shifts are paid, weekly totals will appear here.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={series} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}h`} />
              <Tooltip formatter={(v, name) => (name === 'Volume ($)' ? fmtMoney(v) : name === 'Hours' ? `${v}h` : v)}
                labelFormatter={(l, payload) => (payload && payload[0] ? `${payload[0].payload.week} · ${l}` : l)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="left" dataKey="volume" name="Volume ($)" fill="#6366f1" radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="hours" name="Hours" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
              <Bar yAxisId="right" dataKey="overtime_workers" name="OT workers" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={6} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {overtime.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl" data-testid="agency-pay-overtime-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-amber-200">
            <h4 className="text-sm font-semibold text-amber-900 inline-flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Overtime alerts — over {data.ot_threshold}h in a week</h4>
            <span className="text-xs text-amber-800">{overtime.length} alert{overtime.length === 1 ? '' : 's'}</span>
          </div>
          <ul className="divide-y divide-amber-200 max-h-56 overflow-y-auto">
            {overtime.map((o, i) => (
              <li key={`${o.worker_id}-${o.week}`} className="flex items-center justify-between px-4 py-2 text-sm cursor-pointer hover:bg-amber-100/60"
                onClick={() => setDrill(o.worker_id)} data-testid={`agency-pay-ot-row-${i}`}>
                <div><p className="font-medium text-slate-900">{o.worker_name}</p><p className="text-xs text-amber-800">Week of {o.label} · {o.week}</p></div>
                <p className="font-semibold text-amber-900">{o.hours}h <span className="text-xs font-normal">(+{o.over_by}h)</span></p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h4 className="text-sm font-semibold text-slate-900">Payees this window</h4>
          <span className="text-xs text-slate-500">{fmtMoney(summary.total_volume)} across {summary.total_payouts || 0} payout{(summary.total_payouts || 0) === 1 ? '' : 's'}</span>
        </div>
        {workers.length === 0 ? (
          <p className="p-8 text-center text-slate-500 text-sm" data-testid="agency-pay-workers-empty">No worker payouts recorded yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {workers.map((w, i) => (
              <li key={w.worker_id} role="button" tabIndex={0} onClick={() => setDrill(w.worker_id)} onKeyDown={e => e.key === 'Enter' && setDrill(w.worker_id)}
                className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-slate-50" data-testid={`agency-pay-worker-row-${i}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-fuchsia-400 flex items-center justify-center text-white text-xs font-semibold">{(w.worker_name || '?').charAt(0).toUpperCase()}</div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{w.worker_name}</p>
                    <p className="text-xs text-slate-500">{w.count} payout{w.count === 1 ? '' : 's'} · {w.hours}h{w.avg_hourly ? ` · ~${fmtMoney(w.avg_hourly)}/h` : ''}{w.overtime_weeks ? <span className="text-amber-700 font-semibold"> · {w.overtime_weeks} OT wk</span> : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2"><p className="text-sm font-semibold text-slate-900">{fmtMoney(w.volume)}</p><ChevronRight className="w-4 h-4 text-slate-400" /></div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {drill && <PayeeDrilldown workerId={drill} weeks={weeks} otThreshold={otThreshold} onClose={() => setDrill(null)} />}
    </div>
  );
}

function PayeeDrilldown({ workerId, weeks, otThreshold, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    apiCall(`/api/org/analytics/payouts/worker/${workerId}?weeks=${weeks}&ot_threshold=${otThreshold}`).then(setD)
      .catch(e => { toast.error(e.message || 'Failed to load payee'); onClose(); });
  }, [workerId, weeks, otThreshold]);  

  const exportCsv = () => {
    if (!d) return;
    const rows = [['Section', 'Date / Week', 'Label', 'Amount ($)', 'Hours', 'Detail']];
    d.series.forEach(s => rows.push(['Weekly', s.week, s.label, s.volume, s.hours, s.overtime ? `Overtime +${s.over_by}h` : '']));
    d.payouts.forEach(p => rows.push(['Payout', p.settled_at, p.week, p.amount, '', p.method || '']));
    d.shifts.forEach(s => rows.push(['Shift', s.at, s.week, '', s.hours, `${s.job_title}${s.rate ? ` @ $${s.rate}/h` : ''}`]));
    downloadCsv(`payee-${(d.worker.worker_name || 'worker').replace(/\s+/g, '-')}-${weeks}w.csv`, rows);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={onClose} data-testid="agency-pay-drilldown">
      <div className="w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center gap-3 z-10">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white flex items-center justify-center font-bold">{(d?.worker?.worker_name || '?')[0]}</div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-900 truncate" data-testid="agency-pay-drilldown-name">{d?.worker?.worker_name || 'Loading…'}</p>
            <p className="text-xs text-slate-500 truncate">{d?.worker?.email}{d?.worker?.phone ? ` · ${d.worker.phone}` : ''}</p>
          </div>
          <button onClick={exportCsv} disabled={!d} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-slate-900 text-white disabled:opacity-40" data-testid="agency-pay-drilldown-csv"><Download className="w-3.5 h-3.5" /> CSV</button>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="agency-pay-drilldown-close"><X className="w-5 h-5" /></button>
        </div>
        {!d ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div> : (
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Paid" value={fmtMoney(d.summary.total_volume)} accent />
              <Kpi label="Payouts" value={d.summary.total_payouts} />
              <Kpi label="Hours" value={`${d.summary.total_hours}h`} icon={Clock} />
              <div className={`rounded-xl p-3 border ${d.summary.overtime_weeks ? 'bg-amber-50 border-amber-300' : 'bg-white border-slate-200'}`}>
                <div className="text-xs uppercase tracking-wide text-slate-500">OT weeks</div>
                <p className="mt-1 text-lg font-semibold text-slate-900">{d.summary.overtime_weeks}</p>
                <p className="text-[10px] text-slate-500">{d.summary.avg_hourly ? `~${fmtMoney(d.summary.avg_hourly)}/h effective` : 'no rate data'}</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={d.series} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="l" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}h`} />
                  <Tooltip formatter={(v, name) => (name === 'Paid ($)' ? fmtMoney(v) : `${v}h`)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="l" dataKey="volume" name="Paid ($)" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="r" dataKey="hours" name="Hours" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <Section title={`Weekly breakdown (OT threshold ${d.ot_threshold}h)`}>
              {d.series.map(s => (
                <li key={s.week} className={`flex items-center justify-between px-3 py-2 text-sm ${s.overtime ? 'bg-amber-50' : ''}`}>
                  <span className="text-slate-700">{s.label} <span className="text-xs text-slate-400">{s.week}</span></span>
                  <span className="text-xs text-slate-500">{s.shifts} shift{s.shifts === 1 ? '' : 's'} · {s.hours}h {s.overtime && <span className="text-amber-700 font-semibold">+{s.over_by}h OT</span>}</span>
                  <span className="font-semibold text-slate-900">{fmtMoney(s.volume)}</span>
                </li>
              ))}
            </Section>
            <Section title={`Payouts (${d.payouts.length})`} empty={d.payouts.length === 0 && 'No settled payouts in this window.'}>
              {d.payouts.map((p, i) => (
                <li key={i} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`agency-pay-drilldown-payout-${i}`}>
                  <span className="text-slate-700">{new Date(p.settled_at).toLocaleString()}</span>
                  <span className="text-xs text-slate-500">{p.method || 'payroll'}</span>
                  <span className="font-semibold text-emerald-700">{fmtMoney(p.amount)}</span>
                </li>
              ))}
            </Section>
            <Section title={`Shifts (${d.shifts.length})`} empty={d.shifts.length === 0 && 'No clocked shifts in this window.'}>
              {d.shifts.map((s, i) => (
                <li key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="text-slate-700 truncate">{new Date(s.at).toLocaleDateString()} · {s.job_title || 'Shift'}</span>
                  <span className="text-xs text-slate-500">{s.rate ? `$${s.rate}/h` : ''}</span>
                  <span className="font-semibold text-slate-900">{s.hours}h</span>
                </li>
              ))}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children, empty }) {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <p className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-500 bg-slate-50 border-b border-slate-100">{title}</p>
      {empty ? <p className="px-3 py-4 text-xs text-slate-400">{empty}</p> : <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto">{children}</ul>}
    </div>
  );
}

function Kpi({ label, value, accent = false, icon: Icon, testid }) {
  return (
    <div className={`rounded-xl p-3 border ${accent ? 'bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white border-transparent' : 'bg-white border-slate-200'}`} data-testid={testid}>
      <div className={`flex items-center gap-2 text-xs uppercase tracking-wide ${accent ? 'text-white/80' : 'text-slate-500'}`}>{Icon && <Icon className="w-3.5 h-3.5" />} {label}</div>
      <p className={`mt-1 text-lg font-semibold ${accent ? 'text-white' : 'text-slate-900'}`}>{value}</p>
    </div>
  );
}
